"use client"

import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Activity,
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
  Gauge,
  Lock,
  Play,
  RefreshCw,
  Square,
  Wifi,
  X,
} from "lucide-react"
import customerPortalAxios from "../../utils/customerPortalAxios.ts"
import { PortalStatStrip, type PortalStatItem } from "../employee-portal/shared/PortalStatStrip.tsx"

export type SmartOLTWifiPort = {
  port: string
  wifi_port?: string | null
  ssid?: string | null
  password?: string | null
  auth_mode?: string | null
  admin_state?: string | null
}

export type SmartOLTPortalProfile = {
  mapped: boolean
  onu_external_id?: string | null
  last_sync_at?: string | null
  status?: string | null
  admin_status?: string | null
  onu_type_name?: string | null
  signal_1490?: string | number | null
  signal_1310?: string | number | null
  distance?: string | number | null
  wifi_ports: SmartOLTWifiPort[]
  router_hosts?: RouterHost[]
}

type RouterHost = {
  index?: number
  mac_address?: string | null
  ip_address?: string | null
  address_source?: string | null
  hostname?: string | null
  port?: string | null
  active?: boolean | string | null
}

type GraphPoint = { ts: string; value: number }
type GraphPayload = {
  mapped: boolean
  range: string
  series: {
    signal_rx: GraphPoint[]
    signal_tx: GraphPoint[]
    traffic_up: GraphPoint[]
    traffic_down: GraphPoint[]
  }
}

type GraphType = "hourly" | "daily" | "weekly" | "monthly"
type TrafficSample = {
  elapsed_seconds: number
  remaining_seconds: number
  is_active: boolean
  download_mbps: number
  upload_mbps: number
  download_peak_mbps: number
  upload_peak_mbps: number
  download_avg_mbps: number
  upload_avg_mbps: number
  latency_ms?: number | null
  completed?: boolean
}

const GRAPH_TYPES: Array<{ value: GraphType; label: string; seriesRange: "6h" | "24h" | "7d" | "30d" }> = [
  { value: "hourly", label: "Hourly", seriesRange: "24h" },
  { value: "daily", label: "Daily", seriesRange: "7d" },
  { value: "weekly", label: "Weekly", seriesRange: "30d" },
  { value: "monthly", label: "Monthly", seriesRange: "30d" },
]

const SERIES_EMPTY: GraphPayload["series"] = {
  signal_rx: [],
  signal_tx: [],
  traffic_up: [],
  traffic_down: [],
}

function formatMetric(value: string | number | null | undefined, suffix: string) {
  if (value == null || value === "") return "—"
  const numeric = Number(value)
  if (Number.isFinite(numeric)) return `${numeric.toFixed(1)} ${suffix}`
  return `${value} ${suffix}`
}

function wifiBand(port?: string | null, ssid?: string | null) {
  const text = `${port || ""} ${ssid || ""}`.toLowerCase()
  if (text.includes("5g") || text.includes("5.0") || /\/5\b/.test(text)) return "5 GHz"
  return "2.4 GHz"
}

function isVisibleSsid(ssid?: string | null) {
  const name = (ssid || "").trim()
  if (!name) return false
  return !["hidden", "hidden ssid", "<hidden>"].includes(name.toLowerCase())
}

function hostActiveLabel(value: RouterHost["active"]) {
  if (typeof value === "boolean") return value ? "Yes" : "No"
  const text = String(value ?? "").trim()
  if (!text) return "-"
  return ["1", "true", "yes", "active", "enabled"].includes(text.toLowerCase()) ? "Yes" : text
}

function RouterHostsTable({ hosts }: { hosts: RouterHost[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 bg-portal-tint px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-deep-ocean">
          <Wifi className="h-4 w-4 text-portal-primary" /> Router Hosts
        </h3>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-portal-primary">{hosts.length} connected</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-gray">
            <tr>
              <th className="px-4 py-2">#</th>
              <th className="px-4 py-2">MAC Address</th>
              <th className="px-4 py-2">IP Address</th>
              <th className="px-4 py-2">Address Source</th>
              <th className="px-4 py-2">Hostname</th>
              <th className="px-4 py-2">Port</th>
              <th className="px-4 py-2">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {hosts.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-gray">No router hosts reported.</td></tr>
            ) : hosts.map((host, index) => (
              <tr key={`${host.mac_address || host.ip_address || index}`} className="hover:bg-gray-50/70">
                <td className="px-4 py-2 font-mono text-xs text-slate-gray">{host.index || index + 1}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900">{host.mac_address || "-"}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-900">{host.ip_address || "-"}</td>
                <td className="px-4 py-2 text-gray-700">{host.address_source || "-"}</td>
                <td className="px-4 py-2 font-medium text-gray-900">{host.hostname || "-"}</td>
                <td className="px-4 py-2 text-gray-700">{host.port || "-"}</td>
                <td className="px-4 py-2">
                  <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-700">
                    {hostActiveLabel(host.active)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function chartPath(points: GraphPoint[], width = 560, height = 168, padX = 36, padY = 22) {
  if (points.length < 2) return { line: "", area: "" }
  const values = points.map((p) => Number(p.value)).filter((v) => Number.isFinite(v))
  if (!values.length) return { line: "", area: "" }
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = Math.max(0.1, max - min)
  const coords = points.map((p, idx) => {
    const x = padX + (idx / Math.max(1, points.length - 1)) * (width - padX * 2)
    const y = height - padY - ((Number(p.value) - min) / span) * (height - padY * 2)
    return { x, y }
  })
  const line = coords.map((c, idx) => `${idx === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ")
  const area = `${line} L${coords[coords.length - 1].x.toFixed(1)},${height - padY} L${coords[0].x.toFixed(1)},${height - padY} Z`
  return { line, area }
}

function MiniLineChart({
  title,
  primary,
  secondary,
  primaryLabel,
  secondaryLabel,
  unit,
  imageUrl,
  imageLoading,
}: {
  title: string
  primary: GraphPoint[]
  secondary: GraphPoint[]
  primaryLabel: string
  secondaryLabel: string
  unit: string
  imageUrl: string | null
  imageLoading: boolean
}) {
  const latestPrimary = primary.at(-1)?.value
  const latestSecondary = secondary.at(-1)?.value
  const empty = primary.length === 0 && secondary.length === 0 && !imageUrl
  const primaryPath = chartPath(primary)
  const secondaryPath = chartPath(secondary)

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-portal-tint px-4 py-3">
        <h4 className="text-sm font-semibold text-deep-ocean">{title}</h4>
        <div className="flex gap-3 text-right text-xs">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-gray">{primaryLabel}</p>
            <p className="font-mono text-sm font-semibold tabular-nums text-portal-primary">
              {latestPrimary != null ? `${Number(latestPrimary).toFixed(1)} ${unit}` : "—"}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-gray">{secondaryLabel}</p>
            <p className="font-mono text-sm font-semibold tabular-nums text-amber-700">
              {latestSecondary != null ? `${Number(latestSecondary).toFixed(1)} ${unit}` : "—"}
            </p>
          </div>
        </div>
      </div>
      <div className="relative h-48 bg-white p-3">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="h-full w-full rounded-lg border border-gray-100 bg-white object-contain" />
        ) : empty ? (
          <div className="flex h-full items-center justify-center text-center text-xs text-slate-gray">
            No samples for this range.
          </div>
        ) : (
          <svg viewBox="0 0 560 168" className="h-full w-full">
            {[0, 1, 2, 3].map((i) => (
              <line key={i} x1="36" x2="524" y1={22 + i * 31} y2={22 + i * 31} stroke="#E5E7EB" strokeDasharray="4 4" />
            ))}
            {primaryPath.area && <path d={primaryPath.area} fill="#2A5C8A" fillOpacity="0.08" />}
            {secondaryPath.area && <path d={secondaryPath.area} fill="#D97706" fillOpacity="0.08" />}
            <path d={primaryPath.line} fill="none" stroke="#2A5C8A" strokeWidth="2.25" strokeLinecap="round" />
            <path d={secondaryPath.line} fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
        {imageLoading && (
          <div className="absolute inset-3 flex items-center justify-center rounded-lg bg-white/70">
            <RefreshCw className="h-4 w-4 animate-spin text-portal-primary" />
          </div>
        )}
      </div>
      <div className="flex gap-4 border-t border-gray-50 px-4 py-2 text-[11px] text-slate-gray">
        <span className="inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-portal-primary" /> {primaryLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {secondaryLabel}
        </span>
      </div>
    </div>
  )
}

function PortalTrafficTest() {
  const [sample, setSample] = useState<TrafficSample | null>(null)
  const [history, setHistory] = useState<Array<{ elapsed: number; down: number; up: number }>>([])
  const [running, setRunning] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
  }

  const stop = useCallback(async () => {
    clearTimer()
    const sessionId = sessionIdRef.current
    if (sessionId) {
      try {
        const res = await customerPortalAxios.post(`/public/customer/smartolt/traffic-test/${sessionId}/stop`)
        setSample(res.data)
      } catch {
        // ignore final stop errors
      }
    }
    sessionIdRef.current = null
    setRunning(false)
  }, [])

  useEffect(() => () => {
    clearTimer()
    if (sessionIdRef.current) {
      customerPortalAxios.post(`/public/customer/smartolt/traffic-test/${sessionIdRef.current}/stop`).catch(() => {})
    }
  }, [])

  const start = async () => {
    setBusy(true)
    setError(null)
    setSample(null)
    setHistory([])
    try {
      const res = await customerPortalAxios.post("/public/customer/smartolt/traffic-test/start", { duration_seconds: 30 })
      sessionIdRef.current = res.data.session_id
      setRunning(true)
      const poll = async () => {
        const sid = sessionIdRef.current
        if (!sid) return
        const sampleRes = await customerPortalAxios.get(`/public/customer/smartolt/traffic-test/${sid}/sample`)
        const next = sampleRes.data as TrafficSample
        setSample(next)
        setHistory((prev) => [...prev.slice(-14), { elapsed: next.elapsed_seconds, down: next.download_mbps, up: next.upload_mbps }])
        if (!next.is_active || next.remaining_seconds <= 0 || next.completed) {
          clearTimer()
          sessionIdRef.current = null
          setRunning(false)
        }
      }
      await poll()
      timerRef.current = setInterval(poll, 2000)
    } catch (err: any) {
      setError(err?.response?.data?.error || "Unable to start traffic test.")
      setRunning(false)
    } finally {
      setBusy(false)
    }
  }

  const duration = 30
  const progress = sample ? Math.min(100, (sample.elapsed_seconds / duration) * 100) : 0
  const maxVal = Math.max(10, ...history.map((h) => Math.max(h.down, h.up)), 1)
  const plot = { x: 40, y: 16, w: 480, h: 96 }
  const toPoint = (h: { elapsed: number; down: number; up: number }, key: "down" | "up") => {
    const x = plot.x + Math.min(1, h.elapsed / duration) * plot.w
    const y = plot.y + plot.h - (h[key] / maxVal) * plot.h
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-portal-tint px-4 py-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-deep-ocean">
            <Activity className="h-4 w-4 text-portal-primary" /> On-demand live traffic test
          </h3>
          <p className="text-xs text-slate-gray">30-second download, upload, and latency check.</p>
        </div>
        {running ? (
          <button
            type="button"
            onClick={stop}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-coral-red px-3 text-sm font-medium text-white hover:bg-coral-red/90"
          >
            <Square className="h-3.5 w-3.5" /> Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={start}
            disabled={busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-portal-primary px-3 text-sm font-medium text-white hover:bg-portal-primary-dark disabled:opacity-50"
          >
            {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {sample ? "Run again" : "Run test"}
          </button>
        )}
      </div>

      <div className="space-y-4 p-4">
        {error && <p className="rounded-lg bg-coral-red/5 px-3 py-2 text-xs text-coral-red">{error}</p>}

        {running && (
          <div>
            <div className="mb-1 flex justify-between text-[11px] text-slate-gray">
              <span>{Math.round(sample?.elapsed_seconds || 0)}s elapsed</span>
              <span>{Math.round(sample?.remaining_seconds || 0)}s left</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-portal-accent" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-3">
          <div className="rounded-xl border border-electric-blue/15 bg-electric-blue/5 p-3">
            <p className="flex items-center gap-1 text-[11px] font-medium text-slate-gray">
              <ArrowDown className="h-3.5 w-3.5 text-electric-blue" /> Download
            </p>
            <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-deep-ocean">
              {sample ? sample.download_mbps.toFixed(1) : "—"}
              <span className="ml-1 text-xs font-semibold text-slate-gray">Mbps</span>
            </p>
            <p className="mt-1 text-[11px] text-slate-gray">
              Peak {sample ? sample.download_peak_mbps.toFixed(1) : "—"} · Avg {sample ? sample.download_avg_mbps.toFixed(1) : "—"}
            </p>
          </div>
          <div className="rounded-xl border border-golden-amber/20 bg-golden-amber/5 p-3">
            <p className="flex items-center gap-1 text-[11px] font-medium text-slate-gray">
              <ArrowUp className="h-3.5 w-3.5 text-golden-amber" /> Upload
            </p>
            <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-deep-ocean">
              {sample ? sample.upload_mbps.toFixed(1) : "—"}
              <span className="ml-1 text-xs font-semibold text-slate-gray">Mbps</span>
            </p>
            <p className="mt-1 text-[11px] text-slate-gray">
              Peak {sample ? sample.upload_peak_mbps.toFixed(1) : "—"} · Avg {sample ? sample.upload_avg_mbps.toFixed(1) : "—"}
            </p>
          </div>
          <div className="rounded-xl border border-deep-ocean/10 bg-light-sky/60 p-3">
            <p className="text-[11px] font-medium text-slate-gray">Latency</p>
            <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-deep-ocean">
              {sample?.latency_ms != null ? Number(sample.latency_ms).toFixed(0) : "—"}
              <span className="ml-1 text-xs font-semibold text-slate-gray">ms</span>
            </p>
            <p className="mt-1 text-[11px] text-slate-gray">{running ? "Live sample" : sample ? "Last result" : "Not started"}</p>
          </div>
        </div>

        <div className="h-32 rounded-xl border border-gray-100 bg-gray-50/70 p-2">
          {history.length < 2 ? (
            <div className="flex h-full items-center justify-center text-xs text-slate-gray">
              {running ? "Collecting samples…" : "Run a test to plot live throughput."}
            </div>
          ) : (
            <svg viewBox="0 0 560 128" className="h-full w-full">
              {[0, 1, 2].map((i) => (
                <line key={i} x1="40" x2="520" y1={16 + i * 32} y2={16 + i * 32} stroke="#E5E7EB" strokeDasharray="4 4" />
              ))}
              <polyline fill="none" stroke="#2A5C8A" strokeWidth="2" points={history.map((h) => toPoint(h, "down")).join(" ")} />
              <polyline fill="none" stroke="#D97706" strokeWidth="2" points={history.map((h) => toPoint(h, "up")).join(" ")} />
            </svg>
          )}
        </div>
      </div>
    </div>
  )
}

export function CustomerPortalSmartOLTSection({
  smartolt,
  onRefresh,
}: {
  smartolt: SmartOLTPortalProfile
  onRefresh: () => void
}) {
  const [graphType, setGraphType] = useState<GraphType>("hourly")
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [graphs, setGraphs] = useState<GraphPayload | null>(null)
  const [graphLoading, setGraphLoading] = useState(false)
  const [signalImage, setSignalImage] = useState<string | null>(null)
  const [trafficImage, setTrafficImage] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [visible, setVisible] = useState<Record<string, boolean>>({})
  const [copied, setCopied] = useState<string | null>(null)
  const [resetPort, setResetPort] = useState<SmartOLTWifiPort | null>(null)
  const [newWifiPassword, setNewWifiPassword] = useState("")
  const [confirmWifiPassword, setConfirmWifiPassword] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [resetBusy, setResetBusy] = useState(false)
  const [resetMessage, setResetMessage] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const signalUrlRef = useRef<string | null>(null)
  const trafficUrlRef = useRef<string | null>(null)

  const seriesRange = GRAPH_TYPES.find((item) => item.value === graphType)?.seriesRange || "24h"

  useEffect(() => {
    let cancelled = false
    const revoke = () => {
      if (signalUrlRef.current) URL.revokeObjectURL(signalUrlRef.current)
      if (trafficUrlRef.current) URL.revokeObjectURL(trafficUrlRef.current)
      signalUrlRef.current = null
      trafficUrlRef.current = null
    }

    const load = async () => {
      setGraphLoading(true)
      setImageLoading(true)
      try {
        const graphsRes = await customerPortalAxios.get("/public/customer/smartolt/graphs", {
          params: { range: seriesRange },
        })
        if (!cancelled) setGraphs(graphsRes.data)
      } catch {
        if (!cancelled) setGraphs({ mapped: true, range: seriesRange, series: SERIES_EMPTY })
      } finally {
        if (!cancelled) setGraphLoading(false)
      }

      const loadImage = async (kind: "signal" | "traffic") => {
        const res = await customerPortalAxios.get("/public/customer/smartolt/graph-image", {
          params: { kind, graph_type: graphType },
          responseType: "blob",
        })
        if (res.data?.type && String(res.data.type).includes("json")) return null
        return URL.createObjectURL(res.data)
      }

      try {
        const [signalUrl, trafficUrl] = await Promise.all([
          loadImage("signal").catch(() => null),
          loadImage("traffic").catch(() => null),
        ])
        if (cancelled) {
          if (signalUrl) URL.revokeObjectURL(signalUrl)
          if (trafficUrl) URL.revokeObjectURL(trafficUrl)
          return
        }
        revoke()
        signalUrlRef.current = signalUrl
        trafficUrlRef.current = trafficUrl
        setSignalImage(signalUrl)
        setTrafficImage(trafficUrl)
      } finally {
        if (!cancelled) setImageLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
      revoke()
    }
  }, [graphType, seriesRange, refreshNonce])

  const copyPassword = async (port: SmartOLTWifiPort) => {
    if (!port.password) return
    await navigator.clipboard.writeText(port.password)
    setCopied(port.port)
    setTimeout(() => setCopied(null), 1800)
  }

  const submitPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resetPort) return
    setResetError(null)
    setResetMessage(null)
    if (newWifiPassword.length < 8 || newWifiPassword.length > 32) {
      setResetError("Password must be 8 to 32 characters.")
      return
    }
    if (newWifiPassword !== confirmWifiPassword) {
      setResetError("Passwords do not match.")
      return
    }
    setResetBusy(true)
    try {
      await customerPortalAxios.post("/public/customer/smartolt/wifi-password", {
        port: resetPort.wifi_port || resetPort.port,
        password: newWifiPassword,
      })
      setResetMessage("Update queued. The ONU may take about a minute to apply it.")
      setNewWifiPassword("")
      setConfirmWifiPassword("")
      onRefresh()
    } catch (err: any) {
      setResetError(err?.response?.data?.error || "Unable to update Wi-Fi.")
    } finally {
      setResetBusy(false)
    }
  }

  const visibleWifiPorts = useMemo(
    () => (smartolt.wifi_ports || []).filter((port) => isVisibleSsid(port.ssid)),
    [smartolt.wifi_ports],
  )

  const kpis: PortalStatItem[] = useMemo(
    () => [
      {
        key: "status",
        label: "ONU status",
        value: smartolt.status || "Unknown",
        subValue: smartolt.onu_type_name ? `Model ${smartolt.onu_type_name}` : undefined,
        icon: Gauge,
        tone: String(smartolt.status || "").toLowerCase().includes("online") ? "success" : "default",
      },
      {
        key: "rx",
        label: "Rx 1490",
        value: formatMetric(smartolt.signal_1490, "dBm"),
        icon: ArrowDown,
        tone: "accent",
      },
      {
        key: "tx",
        label: "Tx 1310",
        value: formatMetric(smartolt.signal_1310, "dBm"),
        icon: ArrowUp,
        tone: "warning",
      },
      {
        key: "distance",
        label: "Distance",
        value: formatMetric(smartolt.distance, "m"),
        icon: Activity,
      },
    ],
    [smartolt],
  )

  return (
    <div className="space-y-4">
      <PortalStatStrip items={kpis} columnsMobile={2} columnsDesktop={4} />

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 bg-portal-tint px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-deep-ocean">
              <Gauge className="h-4 w-4 text-portal-primary" /> Signal & traffic graphs
            </h3>
            <p className="text-xs text-slate-gray">Optical power and subscriber throughput for this ONU.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-lg border border-gray-200 bg-white p-1">
              {GRAPH_TYPES.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setGraphType(item.value)}
                  className={`h-8 rounded-md px-2.5 text-xs font-semibold ${
                    graphType === item.value ? "bg-portal-primary text-white" : "text-slate-gray hover:text-gray-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                onRefresh()
                setRefreshNonce((n) => n + 1)
              }}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-slate-gray hover:bg-gray-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${graphLoading || imageLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <MiniLineChart
          title="Optical signal"
          primary={graphs?.series.signal_rx || []}
          secondary={graphs?.series.signal_tx || []}
          primaryLabel="Rx"
          secondaryLabel="Tx"
          unit="dBm"
          imageUrl={signalImage}
          imageLoading={imageLoading}
        />
        <MiniLineChart
          title="Traffic history"
          primary={graphs?.series.traffic_down || []}
          secondary={graphs?.series.traffic_up || []}
          primaryLabel="Down"
          secondaryLabel="Up"
          unit="Mbps"
          imageUrl={trafficImage}
          imageLoading={imageLoading}
        />
      </div>

      <PortalTrafficTest />

      <RouterHostsTable hosts={smartolt.router_hosts || []} />

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="mb-0 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-portal-tint px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-deep-ocean">
              <Wifi className="h-4 w-4 text-portal-primary" /> SSID configuration
            </h3>
            <p className="text-xs text-slate-gray">Wi-Fi networks provisioned on your ONU.</p>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-portal-primary">
            {visibleWifiPorts.length} SSID{visibleWifiPorts.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-4">
          {visibleWifiPorts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-slate-gray">
              No Wi-Fi SSID is reported for this ONU.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {visibleWifiPorts.map((port) => {
                const key = port.port || port.ssid || "wifi"
                const isVisible = visible[key]
                return (
                  <div key={key} className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{port.ssid || "Hidden SSID"}</p>
                        <p className="mt-0.5 text-xs text-slate-gray">
                          {wifiBand(port.port, port.ssid)} · {port.auth_mode || "WPA2"}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          String(port.admin_state || "enabled").toLowerCase() === "enabled"
                            ? "bg-emerald-green/10 text-emerald-green"
                            : "bg-gray-100 text-slate-gray"
                        }`}
                      >
                        {port.admin_state || "Enabled"}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <Lock className="h-3.5 w-3.5 text-slate-gray" />
                      <span className="min-w-0 flex-1 truncate font-mono text-sm text-gray-900">
                        {port.password ? (isVisible ? port.password : "••••••••••••") : "Password unavailable"}
                      </span>
                      {port.password && (
                        <>
                          <button
                            type="button"
                            onClick={() => setVisible((prev) => ({ ...prev, [key]: !prev[key] }))}
                            className="rounded p-1 text-slate-gray hover:bg-gray-100"
                            aria-label={isVisible ? "Hide password" : "Show password"}
                          >
                            {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => copyPassword(port)}
                            className="rounded p-1 text-slate-gray hover:bg-gray-100"
                            aria-label="Copy password"
                          >
                            {copied === port.port ? <CheckCircle className="h-4 w-4 text-emerald-green" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setResetPort(port)
                        setNewWifiPassword("")
                        setConfirmWifiPassword("")
                        setResetMessage(null)
                        setResetError(null)
                      }}
                      className="mt-3 h-9 w-full rounded-lg bg-portal-primary text-sm font-medium text-white hover:bg-portal-primary-dark"
                    >
                      Reset Wi-Fi password
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {resetPort && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-gray-100 bg-portal-primary px-4 py-3 text-white">
              <div>
                <h3 className="text-sm font-semibold">Reset Wi-Fi password</h3>
                <p className="text-[11px] text-white/80">{resetPort.ssid || resetPort.port}</p>
              </div>
              <button
                type="button"
                onClick={() => setResetPort(null)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={submitPasswordReset} className="space-y-3 p-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-gray">Network name</label>
                <div className="min-h-9 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900">
                  {resetPort.ssid || "Current Wi-Fi network"}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-gray">New password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    aria-label="New password"
                    value={newWifiPassword}
                    onChange={(e) => setNewWifiPassword(e.target.value)}
                    minLength={8}
                    maxLength={32}
                    className="h-9 w-full rounded-lg border border-gray-200 px-3 pr-9 font-mono text-sm focus:border-portal-accent focus:outline-none focus:ring-2 focus:ring-portal-accent/40"
                    placeholder="8–32 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 px-2.5 text-slate-gray"
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-gray">Confirm password</label>
                <input
                  type={showNewPassword ? "text" : "password"}
                  aria-label="Confirm password"
                  value={confirmWifiPassword}
                  onChange={(e) => setConfirmWifiPassword(e.target.value)}
                  minLength={8}
                  maxLength={32}
                  className="h-9 w-full rounded-lg border border-gray-200 px-3 font-mono text-sm focus:border-portal-accent focus:outline-none focus:ring-2 focus:ring-portal-accent/40"
                  required
                />
              </div>
              {resetError && <p className="rounded-lg bg-coral-red/5 px-3 py-2 text-xs text-coral-red">{resetError}</p>}
              {resetMessage && <p className="rounded-lg bg-emerald-green/5 px-3 py-2 text-xs text-emerald-green">{resetMessage}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setResetPort(null)}
                  className="h-9 rounded-lg border border-gray-200 px-3 text-sm text-slate-gray hover:bg-gray-50"
                >
                  {resetMessage ? "Close" : "Cancel"}
                </button>
                {!resetMessage && (
                  <button disabled={resetBusy} className="h-9 rounded-lg bg-portal-primary px-3 text-sm font-medium text-white disabled:opacity-50">
                    {resetBusy ? "Queueing…" : "Update password"}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
